/**
 * One control per FIELD KIND — never a form per block.
 *
 * ⚠️ THIS FILE IS WHERE THE REGISTRY'S CENTRAL CLAIM IS EITHER TRUE OR FALSE. `@ksa/model`'s
 * `prop-types.ts` says a block ships with zero editor code; that is only true if the editor renders
 * a block's form by walking its `FieldSpec[]` and dispatching on `type.kind`. The moment one block
 * gets a bespoke form here, every subsequent block needs one too, and the registry becomes
 * documentation rather than machinery.
 *
 * There are nine kinds and there are nine controls below. Adding a tenth block type must not add a
 * tenth control.
 *
 * > ⚠️ **THIS SAID EVERY TEXT CONTROL EDITS BOTH LANGUAGES AT ONCE — TWO SIDE-BY-SIDE INPUTS,
 * > NEVER A LANGUAGE TAB — AND IT NO LONGER DOES.** Changed on request. The reasoning is kept
 * > because the risk it named is real and is now carried by something else: a tab means the Arabic
 * > half of a site is a place you have to *remember to go*, and a half-translated site is the most
 * > common way a bilingual builder ships something embarrassing.
 * >
 * > Two inputs per field did not survive contact with the drawer. Side by side they need about
 * > 28rem; in a sheet at the bottom of a 360 px phone they stack, so every field became two rows
 * > and a five-field block became a ten-row scroll — and the second row was in a script most
 * > customers were not currently writing in.
 *
 * ⚠️ SO THE GAP IS NOW REPORTED RATHER THAN DISPLAYED, AND THAT IS THE TRADE. One input, in the
 * language the interface is in, plus two markers that the old layout got for free:
 *
 *   - **missing** — the other language is empty. `isPartial` already reported this at publish.
 *   - **behind** — the other language exists but was written against older copy. That is `stale` in
 *     `@ksa/model`, and it exists *because* the customer can no longer see what they are putting
 *     out of date.
 *
 * Both offer to fix themselves, which side-by-side inputs never did.
 */

import { useState } from 'react'
import {
  EMPTY_TEXT,
  defaultRow,
  edited,
  isLit,
  translated,
  type FieldSpec,
  type ImageRef,
  type Lang,
  type Link,
  type Page,
  type Text,
} from '@ksa/model'
import { GROUND_ROLES } from '@ksa/model'
import { Button, Choice, Field, Input, Menu, Select, Textarea } from './components.tsx'
import { useLang } from './lang.tsx'

export type FieldContext = {
  /** The pages a `link` field may point at. */
  pages: Page[]
  /** Open the image chooser for a field. The editor owns the upload/generate flow; this package
   *  only knows that a picture is chosen somewhere else. */
  onPickImage?: (current: ImageRef, set: (next: ImageRef) => void) => void
  /**
   * Turn a stored object key into something an `<img>` can load.
   *
   * ⚠️ IT IS PASSED IN RATHER THAN BUILT HERE, for the same reason `listTargets.ts` lives in the app:
   * `/api/media/<key>` is a route the builder happens to serve, and `@ksa/ui` knowing it would make
   * the design system depend on the API's URL space. The renderer already takes this exact function
   * for the same reason — publish resolves keys to `/_m/<key>` in R2 and the editor to the API.
   */
  mediaUrl?: (key: string) => string
  /**
   * Translate one string. The editor owns the request; this package only knows that a translation
   * comes from somewhere and may fail.
   *
   * ⚠️ IT RETURNS A STRING RATHER THAN WRITING THE VALUE, so the control decides what to do with it
   * — which is `translated()`, not `edited()`. The difference is whether accepting a translation
   * marks the SOURCE language as behind, and getting that wrong makes the debt flag oscillate for
   * ever instead of settling.
   */
  onTranslate?: (args: { source: string; from: 'en' | 'ar'; to: 'en' | 'ar'; label: string }) => Promise<string>
}

type ControlProps = {
  spec: FieldSpec
  value: unknown
  onChange: (next: unknown) => void
  ctx: FieldContext
}

/**
 * The one control the whole product is built on: a field of copy, in the language being worked in.
 *
 * ⚠️ IT WRITES THROUGH `edited()` RATHER THAN SPREADING THE OBJECT ITSELF. That function owns the
 * rule for what an edit does to the other language, and it is deliberately asymmetric so the flag
 * converges — a second copy of it here would be the copy that gets it wrong. See `@ksa/model`.
 */
function TextPair({
  value,
  onChange,
  multiline,
  label,
  hint,
  ctx,
}: {
  value: Text
  onChange: (next: Text) => void
  multiline?: boolean
  label: string
  hint?: string
  ctx: FieldContext
}) {
  const { t, lang } = useLang()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const current = value && typeof value === 'object' ? value : EMPTY_TEXT

  // A `{lit}` value is one string used in both languages — a brand name, a phone number. It gets one
  // control, because splitting it would invite somebody to make the two differ, which is the one
  // thing `lit` means they should not.
  if (isLit(current)) {
    const Control = multiline ? Textarea : Input
    return (
      <Field label={label} hint={hint}>
        {(a) => <Control {...a} value={current.lit} onChange={(e) => onChange({ lit: e.target.value })} />}
      </Field>
    )
  }

  const Control = multiline ? Textarea : Input
  const other: Lang = lang === 'en' ? 'ar' : 'en'
  const otherName = other === 'ar' ? t('Arabic', 'العربية') : t('English', 'الإنجليزية')

  const behind = current.stale === other
  const missing = Boolean(current[lang]) && !current[other]
  const canTranslate = Boolean(ctx.onTranslate) && Boolean(current[lang]) && (behind || missing)

  const fill = async () => {
    if (!ctx.onTranslate) return
    setBusy(true)
    setFailed(false)
    try {
      const text = await ctx.onTranslate({ source: current[lang], from: lang, to: other, label })
      // ⚠️ `translated`, NOT `edited`. Filling in the other language settles the debt; treating it as
      // an ordinary edit would immediately mark THIS language as behind, and the customer would be
      // asked to translate back the copy they just wrote.
      onChange(translated(current, other, text))
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="u-stack u-stack--tight">
      <Field label={label} hint={hint}>
        {(a) => (
          <Control
            {...a}
            /*
             * ⚠️ `dir` AND `lang` ON THE INPUT ITSELF, STILL — and it matters more now than it did
             * with two inputs, not less. The field follows the interface language, so these agree
             * with it today; they are declared anyway because the day a field is pinned to one
             * language, an undeclared input inherits the wrong direction and the caret starts at the
             * wrong end. That is the detail that decides whether a bilingual editor is usable.
             */
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
            lang={lang}
            value={current[lang]}
            onChange={(e) => onChange(edited(current, lang, e.target.value))}
          />
        )}
      </Field>

      {/*
        ⚠️ THE OTHER LANGUAGE IS REPORTED EVEN THOUGH IT IS NOT SHOWN, which is the entire debt this
        layout took on when it stopped showing both. A field that silently holds stale Arabic looks
        finished, and the customer finds out from a visitor.
      */}
      {(behind || missing) && (
        <div className="u-row u-field__debt">
          <span className="u-field__debt-text">
            {missing
              ? t(`No ${otherName} yet`, `لا توجد ${otherName} بعد`)
              : t(`${otherName} is out of date`, `${otherName} لم تعد مطابقة`)}
          </span>
          {canTranslate && (
            <Button variant="quiet" size="sm" disabled={busy} onClick={() => void fill()}>
              {busy ? t('Translating…', 'جارٍ الترجمة…') : t('Translate', 'ترجم')}
            </Button>
          )}
        </div>
      )}
      {failed && (
        <span className="u-field__hint u-field__hint--bad">
          {t('That could not be translated. Try again, or write it yourself.', 'تعذّرت الترجمة. أعد المحاولة أو اكتبها بنفسك.')}
        </span>
      )}
    </div>
  )
}

/**
 * One line: what this field is, and what it currently says, with a way in.
 *
 * ⚠️ IT IS A SUMMARY AND A BUTTON, NOT A DISABLED CONTROL. What somebody wants to know about a
 * button's destination nine times out of ten is what it already is; changing it is the tenth. So the
 * answer is the thing on screen and the controls are one press away — which is one row where the
 * form was three.
 */
function SummaryRow({
  label,
  value,
  empty,
  action,
  onAction,
}: {
  label: string
  value: string
  /** Whether `value` is a prompt ("Nothing yet") rather than an answer, and should read as one. */
  empty?: boolean
  action: string
  onAction: () => void
}) {
  return (
    <div className="u-summary">
      {/*
        ⚠️ THE VALUE IS ITS OWN ELEMENT INSIDE THE LABEL'S ROW, NOT ONE INTERPOLATED STRING.
        «Button goes to: Page (About us)» reads as one sentence and has to look like two things —
        what the field is, and what it currently says — because only the second one ever changes.
      */}
      <span className="u-field__label">
        {label}
        {': '}
        <span className={`u-summary__value ${empty ? 'u-summary__value--empty' : ''}`}>{value}</span>
      </span>
      <Button variant="quiet" size="sm" onClick={onAction}>
        {action}
      </Button>
    </div>
  )
}

/**
 * Where a button goes.
 *
 * ⚠️ IT IS COLLAPSED, AND THAT IS BECAUSE IT IS A DECISION RATHER THAN A SENTENCE. A heading is
 * edited by typing at it; a destination is chosen once and then looked at. Open, it is a group of
 * choices and a second field, sitting permanently under the two words somebody actually came to
 * change.
 *
 * ⚠️ AND IT OPENS ITSELF WHEN THERE IS NOTHING TO SUMMARISE. «Nothing yet» is not a summary, it is a
 * prompt, and on `cta` this field is `requiredForPublish` — so putting the unmade decision one tap
 * further away is the one case where collapsing costs something.
 *
 * ⚠️ THE TWO CHOICES ARE RADIOS RATHER THAN DROPDOWNS BECAUSE BOTH SETS ARE SHORT AND WORTH SEEING.
 * A dropdown hides its options: somebody who has never opened it does not know that "another
 * website" exists, and on a small site "which page" is three or four names that fit on the screen
 * they are already looking at. See `Choice`.
 */
function LinkControl({ value, onChange, ctx, label }: { value: Link; onChange: (n: Link) => void; ctx: FieldContext; label: string }) {
  const { t, r } = useLang()
  const link: Link = value && typeof value === 'object' ? value : { kind: 'none' }
  const [open, setOpen] = useState(link.kind === 'none')

  /*
   * ⚠️ THE PAGE IS LOOKED UP RATHER THAN REMEMBERED. A link holds a `pageId` and never a title
   * (§2.5) — which is what makes renaming a page free — so the name shown here has to be resolved
   * every render, and a page that has been deleted has no name to resolve. Saying so is better than
   * printing an id: the link is broken, `validateSite` reports it at publish, and this is where
   * somebody would notice first.
   */
  const page = link.kind === 'page' ? ctx.pages.find((p) => p.id === link.pageId) : undefined

  /*
   * ⚠️ THE PAGE LIST OPENS WHEN THE PAGE WAS NOT CHOSEN, NOT WHENEVER THE DESTINATION IS A PAGE.
   * Switching the destination TO a page picks the first one as a placeholder — somebody has to be
   * selected for the radio group to have a value — and collapsing that immediately would report
   * «Which page: Home» as though it were a decision. So the switch opens it, and choosing closes it.
   */
  const [picking, setPicking] = useState(false)

  const summary =
    link.kind === 'page'
      ? page
        ? t(`Page (${r(page.title)})`, `صفحة (${r(page.title)})`)
        : t('A page that no longer exists', 'صفحة لم تعد موجودة')
      : link.kind === 'external'
        ? t(`Another website (${hostOf(link.href)})`, `موقع آخر (${hostOf(link.href)})`)
        : t('Nothing yet', 'لا شيء بعد')

  return (
    <div className="u-stack u-stack--tight">
      <SummaryRow
        label={label}
        value={summary}
        empty={link.kind === 'none'}
        action={open ? t('Done', 'تم') : t('Edit', 'تحرير')}
        onAction={() => setOpen((o) => !o)}
      />

      {open && (
        <>
          <Choice
            legend={t('Destination', 'الوجهة')}
            value={link.kind}
            options={[
              { value: 'none' as const, label: t('Nothing yet', 'لا شيء بعد') },
              { value: 'page' as const, label: t('A page on this site', 'صفحة في هذا الموقع') },
              { value: 'external' as const, label: t('Another website', 'موقع آخر') },
            ]}
            onChange={(kind) => {
              if (kind === 'none') onChange({ kind: 'none' })
              else if (kind === 'page') {
                setPicking(true)
                onChange({ kind: 'page', pageId: ctx.pages[0]?.id ?? '' })
              } else onChange({ kind: 'external', href: '' })
            }}
          />

          {link.kind === 'page' &&
            (picking || !page ? (
              <Choice
                legend={t('Which page', 'أي صفحة')}
                value={link.pageId}
                options={ctx.pages.map((p) => ({ value: p.id, label: r(p.title) }))}
                onChange={(pageId) => {
                  // Choosing is the end of choosing, exactly as it is in the dropdown this replaced.
                  setPicking(false)
                  onChange({ kind: 'page', pageId })
                }}
              />
            ) : (
              <SummaryRow
                label={t('Which page', 'أي صفحة')}
                value={r(page.title)}
                action={t('Edit', 'تحرير')}
                onAction={() => setPicking(true)}
              />
            ))}

          {link.kind === 'external' && (
            <Field
              label={t('Web address', 'عنوان الموقع')}
              hint={t('Must start with https://', 'يجب أن يبدأ بـ ‎https://‎')}
            >
              {(a) => (
                <Input
                  {...a}
                  dir="ltr"
                  inputMode="url"
                  placeholder="https://"
                  value={link.href}
                  onChange={(e) => onChange({ kind: 'external', href: e.target.value })}
                />
              )}
            </Field>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The recognisable part of a web address.
 *
 * ⚠️ IT NEVER THROWS, BECAUSE IT IS GIVEN WHATEVER SOMEBODY HAS TYPED SO FAR. `new URL('htt')` is an
 * exception, and this runs on every keystroke of a field whose hint is "must start with https://" —
 * so a half-typed address falls back to the raw text rather than taking the drawer down with it.
 */
function hostOf(href: string): string {
  try {
    return new URL(href).host || href
  } catch {
    return href.trim() || '—'
  }
}

function ImageControl({ value, onChange, ctx, spec }: ControlProps) {
  const { t, r } = useLang()
  const image = (value ?? { status: 'placeholder', description: EMPTY_TEXT, orientation: 'landscape' }) as ImageRef
  const label = r(spec.label)

  return (
    <div className="u-stack u-stack--tight">
      <span className="u-field__label">{label}</span>
      {image.status === 'placeholder' ? (
        <>
          {/*
            > ⚠️ **THIS WAS A DESCRIPTION FIELD — "describe it now, get the picture later" — AND THE
            > "LATER" IS NOT COMING.** On-demand image generation was considered and dropped: a
            > customer-prompted image generator on a platform anyone can sign up to is an abuse
            > surface with real moderation obligations, for a picture the customer can simply take.
            >
            > So the field is gone. It asked people to write a paragraph describing a photograph that
            > nothing would ever produce — busywork dressed as a feature, and the sort of thing that
            > survives for months because it looks like progress. Uploading is the only path now, and
            > it is the only control here.
          */}
          <p className="u-small u-faint">
            {t('No picture yet.', 'لا توجد صورة بعد.')}
          </p>
          <div className="u-row">
            <Button variant="quiet" size="sm" onClick={() => ctx.onPickImage?.(image, onChange)}>
              {t('Upload a picture', 'ارفع صورة')}
            </Button>
          </div>
        </>
      ) : (
        <>
          {/*
            ⚠️ THE PICTURE IS THE ANSWER TO "WHICH PICTURE IS THIS", AND THE KEY NEVER WAS. The field
            showed `io5DDStuiJRsk3XYbIdF/41c202266eae626c99704f8…` — a string nobody can read, that
            wraps over two lines, and that answers a question nobody asked, on a sheet whose whole
            argument is that it leaves the page on screen. A customer with four uploads on a page
            cannot tell them apart from their keys and can tell them apart instantly from 64 pixels.
            The key is still on the element, as its `title`, for the one person who ever wants it.
          */}
          <div className="u-row u-row--between">
            <span className="u-thumb" title={image.key}>
              {ctx.mediaUrl ? (
                <img className="u-thumb__img" src={ctx.mediaUrl(image.key)} alt="" loading="lazy" />
              ) : (
                <span className="u-thumb__img" />
              )}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                onChange({ status: 'placeholder', description: image.alt, orientation: image.orientation })
              }
            >
              {t('Remove', 'إزالة')}
            </Button>
          </div>
          {/*
            ⚠️ ALT TEXT IS A REQUIRED FIELD ON AN UPLOADED PICTURE, not an advanced option. It is what
            a blind visitor to the customer's shop hears, and it is what a search engine reads. Asking
            for it at upload — while the person still remembers what the picture is of — is the only
            moment it will ever be written.
          */}
          <TextPair
            label={t('Describe this picture for people who cannot see it', 'صِف هذه الصورة لمن لا يستطيع رؤيتها')}
            ctx={ctx}
            value={image.alt}
            onChange={(alt) => onChange({ ...image, alt })}
          />
        </>
      )}
      <Field label={t('Shape', 'الشكل')}>
        {(a) => (
          <Select {...a} value={image.orientation} onChange={(e) => onChange({ ...image, orientation: e.target.value })}>
            <option value="landscape">{t('Wide', 'عريضة')}</option>
            <option value="portrait">{t('Tall', 'طولية')}</option>
            <option value="square">{t('Square', 'مربّعة')}</option>
          </Select>
        )}
      </Field>
    </div>
  )
}

/**
 * The rows of a repeater, as data plus the two operations that are about the LIST rather than about a
 * row: how many there are, and adding one.
 */
function rowsOf(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : []
}

/**
 * ONE row of a repeater — its sub-fields, and what can be done to the row itself.
 *
 * ⚠️ IT IS EXPORTED BECAUSE THE DRAWER OPENS ON A SINGLE ROW. A customer who taps Friday's opening
 * hours in the preview means Friday's opening hours, and the sheet shows that row and nothing else —
 * the same rule the single-FIELD drawer already follows (CLAUDE.md §3.3), one level down. Rendering
 * the whole list scrolled to a row would be a sheet you have to search on a phone.
 *
 * ⚠️ IT TAKES THE WHOLE LIST AND AN INDEX, NOT THE ROW. Move, remove and edit are all writes to the
 * ARRAY, and a component holding only its own row would have to hand three callbacks back up to
 * something that could do them — which is where an off-by-one about which row is being removed lives.
 */
export function ListItem({
  spec,
  value,
  index,
  onChange,
  ctx,
  onMoved,
  onRemoved,
  onAll,
}: {
  spec: FieldSpec
  /** The whole list, as the block holds it. */
  value: unknown
  index: number
  onChange: (next: unknown) => void
  ctx: FieldContext
  /**
   * ⚠️ THE ROW BEING EDITED IS AN INDEX, SO MOVING IT HAS TO SAY WHERE IT WENT. A drawer open on row 2
   * that swaps with row 3 and is told nothing is now showing row 3's fields under a thumb that just
   * pressed "move down" — the copy under the customer's hands changes to somebody else's.
   */
  onMoved?: (to: number) => void
  /** Called when this row stops existing, so whatever was showing it can stop. */
  onRemoved?: () => void
  /**
   * Back to the whole list.
   *
   * ⚠️ IT IS THE SAME «label: value, Edit» LINE AS EVERY OTHER COLLAPSED THING IN THE DRAWER, and it
   * replaced a separate "All of these" button sitting above a row of three icon buttons. Two
   * controls in two places, one of which was an unlabelled ↑ ↓ ✕, for a sheet whose whole argument is
   * that it leaves the page on screen. When it is absent — the row is being shown inside the list
   * itself — there is nowhere to go back to and the line is just the row's name.
   */
  onAll?: () => void
}) {
  const { t, r } = useLang()
  if (spec.type.kind !== 'list') return null
  const item = spec.type
  const rows = rowsOf(value)
  const row = rows[index]
  if (!row) return null

  const move = (dir: -1 | 1) => {
    const to = index + dir
    if (to < 0 || to >= rows.length) return
    const next = [...rows]
    const held = next[index]!
    next[index] = next[to]!
    next[to] = held
    onChange(next)
    onMoved?.(to)
  }

  return (
    <div className="u-stack u-stack--tight">
      <div className="u-summary">
        <span className="u-field__label">
          {r(spec.label)}
          {': '}
          <span className="u-summary__value">
            {t(`${index + 1} of ${rows.length}`, `${index + 1} من ${rows.length}`)}
          </span>
        </span>
        <div className="u-row" style={{ gap: 'var(--u-s1)' }}>
          {/*
            ⚠️ MOVING AND REMOVING ARE IN THE MENU BECAUSE THEY ARE NOT WHAT SOMEBODY CAME HERE TO DO.
            They were three unlabelled icon buttons — ↑ ↓ ✕ — beside a heading, which is a toolbar
            competing with the one input the customer tapped a row to reach. A ⋯ is one target, and
            the actions inside it have names.

            ⚠️ "MOVE UP" AND "MOVE DOWN", NOT DRAG. Drag-and-drop is not operable by keyboard without
            a second implementation, and on a phone it competes with scrolling — which is where most
            of this product's editing happens.
          */}
          <Menu
            up
            label={t('More', 'المزيد')}
            actions={[
              { label: t('Move up', 'تحريك لأعلى'), onClick: () => move(-1), disabled: index === 0 },
              { label: t('Move down', 'تحريك لأسفل'), onClick: () => move(1), disabled: index === rows.length - 1 },
              'rule',
              {
                label: t('Remove this one', 'إزالة هذا'),
                danger: true,
                onClick: () => {
                  onChange(rows.filter((_, k) => k !== index))
                  onRemoved?.()
                },
              },
            ]}
          />
          {onAll && (
            <Button variant="quiet" size="sm" onClick={onAll}>
              {t('Edit', 'تحرير')}
            </Button>
          )}
        </div>
      </div>

      {item.item.map((sub) => (
        <Control
          key={sub.key}
          spec={sub}
          value={row[sub.key]}
          ctx={ctx}
          onChange={(next) => onChange(rows.map((other, k) => (k === index ? { ...other, [sub.key]: next } : other)))}
        />
      ))}
    </div>
  )
}

function ListControl({ spec, value, onChange, ctx }: ControlProps) {
  const { t, r } = useLang()
  const [open, setOpen] = useState<number | null>(0)
  if (spec.type.kind !== 'list') return null
  const item = spec.type
  const rows = rowsOf(value)
  const atMax = item.max !== undefined && rows.length >= item.max

  return (
    <div className="u-stack u-stack--tight">
      <div className="u-row u-row--between">
        <span className="u-field__label" style={{ marginBlockEnd: 0 }}>
          {r(spec.label)}
        </span>
        <span className="u-tiny u-faint">
          {rows.length}
          {item.max !== undefined ? ` / ${item.max}` : ''}
        </span>
      </div>

      {rows.map((_, i) => (
        <div key={i} className="u-card" style={{ padding: 'var(--u-s3)' }}>
          <button
            type="button"
            className="u-btn u-btn--ghost u-btn--sm"
            aria-expanded={open === i}
            onClick={() => setOpen(open === i ? null : i)}
          >
            {t(`Item ${i + 1}`, `العنصر ${i + 1}`)}
          </button>
          {open === i && (
            <div style={{ marginBlockStart: 'var(--u-s3)' }}>
              <ListItem
                spec={spec}
                value={value}
                index={i}
                onChange={onChange}
                ctx={ctx}
                onMoved={(to) => setOpen(to)}
                onRemoved={() => setOpen(null)}
              />
            </div>
          )}
        </div>
      ))}

      <div className="u-row">
        <Button
          variant="quiet"
          size="sm"
          onClick={() => {
            onChange([...rows, defaultRow(item.item)])
            setOpen(rows.length)
          }}
          disabled={atMax}
        >
          {t('Add', 'إضافة')}
        </Button>
        {atMax && (
          // The reason is shown, not just the disabled state. A control that is off with no
          // explanation reads as broken.
          <span className="u-tiny u-faint">{t(`That is the most this block can hold.`, 'هذا أقصى ما يمكن لهذه الكتلة استيعابه.')}</span>
        )}
      </div>
    </div>
  )
}

/** The dispatcher. Nine kinds, nine controls, no block-specific branch anywhere. */
export function Control({ spec, value, onChange, ctx }: ControlProps) {
  const { r, t } = useLang()
  const label = r(spec.label)
  const hint = spec.hint ? r(spec.hint) : undefined

  switch (spec.type.kind) {
    case 'text':
      return <TextPair label={label} hint={hint} ctx={ctx} value={(value ?? EMPTY_TEXT) as Text} onChange={onChange} />

    case 'longText':
      return <TextPair label={label} hint={hint} multiline ctx={ctx} value={(value ?? EMPTY_TEXT) as Text} onChange={onChange} />

    case 'image':
      return <ImageControl spec={spec} value={value} onChange={onChange} ctx={ctx} />

    case 'link':
      return <LinkControl label={label} value={value as Link} onChange={onChange} ctx={ctx} />

    case 'colorToken':
      return (
        <Field label={label} hint={hint}>
          {(a) => (
            <Select {...a} value={String(value ?? 'surface')} onChange={(e) => onChange(e.target.value)}>
              {/* ⚠️ ONLY GROUNDS THAT HAVE A TEXT PARTNER. This list is `GROUND_ROLES` from the model
                  rather than a hand-written set — offering a background with no `on-` colour is the
                  bug that put 1.00:1 text on the first block of every site in the predecessor. */}
              {GROUND_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role === 'brand' ? t('Brand colour', 'لون العلامة') : t('Page background', 'خلفية الصفحة')}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )

    case 'enum':
      return (
        <Field label={label} hint={hint}>
          {(a) => (
            <Select {...a} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
              {spec.type.kind === 'enum' &&
                spec.type.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {r(o.label)}
                  </option>
                ))}
            </Select>
          )}
        </Field>
      )

    case 'number': {
      const min = spec.type.min
      const max = spec.type.max
      return (
        <Field label={label} hint={hint}>
          {(a) => (
            <Input
              {...a}
              type="number"
              inputMode="numeric"
              min={min}
              max={max}
              value={typeof value === 'number' ? value : ''}
              onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
            />
          )}
        </Field>
      )
    }

    case 'boolean':
      return (
        <label className="u-row" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <span>{label}</span>
        </label>
      )

    case 'list':
      return <ListControl spec={spec} value={value} onChange={onChange} ctx={ctx} />
  }
}
